import {useCallback, useEffect, useRef, useState} from 'react'

export const EXPORT_MENU_ITEMS: {
  key: string
  label: string
  icon: string
  action: 'download' | 'saveToCloud'
}[] = [
  {key: 'download', label: 'Download', icon: 'fas fa-download', action: 'download'},
  {
    key: 'save-to-cloud',
    label: 'Save to cloud…',
    icon: 'fas fa-cloud-arrow-up',
    action: 'saveToCloud',
  },
]

export interface ExportChatDropdownProps {
  onDownload: () => void
  onSaveToCloud: () => void
  /** Why Save to cloud is off here, if it is. */
  saveDisabledReason?: string
}

/** The chat header's export menu: same shape as the Import Data menu, in the header's styling. */
export const ExportChatDropdown = ({
  onDownload,
  onSaveToCloud,
  saveDisabledReason,
}: ExportChatDropdownProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open, close])

  const disabledReason = (item: (typeof EXPORT_MENU_ITEMS)[number]) =>
    item.action === 'saveToCloud' ? saveDisabledReason : undefined

  const handleItemClick = (item: (typeof EXPORT_MENU_ITEMS)[number]) => {
    if (disabledReason(item)) {
      return
    }
    close()
    switch (item.action) {
      case 'download':
        onDownload()
        break
      case 'saveToCloud':
        onSaveToCloud()
        break
    }
  }

  return (
    <div className='ai-chat-v2-sidebar__dropdown' ref={dropdownRef}>
      <button
        type='button'
        className={`ai-chat-v2-sidebar__icon-btn ${open ? 'active' : ''}`}
        data-testid='omics-ai-chat-export-btn'
        onClick={() => setOpen((prev) => !prev)}
        title='Export chat as Markdown'
        aria-label='Export chat as Markdown'
        aria-expanded={open}
      >
        <i className='fas fa-download' />
      </button>
      {open && (
        <div className='ai-chat-v2-sidebar__dropdown-menu'>
          {EXPORT_MENU_ITEMS.map((item) => (
            <button
              key={item.key}
              type='button'
              className={`ai-chat-v2-sidebar__dropdown-item${
                disabledReason(item) ? ' ob-toolbar-disabled' : ''
              }`}
              aria-disabled={disabledReason(item) ? true : undefined}
              title={disabledReason(item)}
              data-testid={`omics-ai-chat-export-${item.key}`}
              onClick={() => handleItemClick(item)}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
